/**
 * Token Refresh Manager
 *
 * Shared module for automatic JWT token refresh across all API clients.
 * Handles:
 * - Token refresh with queue management for concurrent requests
 * - Prevention of duplicate refresh calls
 * - Centralized refresh state
 * - Proactive token refresh before expiration
 */

import { useAuthStore } from '@/features/auth/stores/useAuthStore';

const SOKA_API_URL = import.meta.env.VITE_SOKA_API_URL ?? 'https://dev.soka.club/api';

// Refresh token 5 minutes before it expires
const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000;

// Response types for token refresh
interface TokenResponse {
  accessToken: string;
  refreshToken: string;
}

interface WrappedTokenResponse {
  data?: TokenResponse;
}

// Queue for requests waiting for token refresh
interface QueuedRequest {
  resolve: (token: string | null) => void;
  reject: (error: unknown) => void;
}

let isRefreshing = false;
let failedQueue: QueuedRequest[] = [];
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Decode JWT token to extract expiration time
 * JWTs are base64-encoded JSON, format: header.payload.signature
 */
function decodeJwtExpiration(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode payload (second part)
    const payload = JSON.parse(atob(parts[1])) as { exp?: number };

    // Return expiration time in milliseconds
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    console.warn('[TokenRefresh] Failed to decode JWT expiration');
    return null;
  }
}

/**
 * Schedule proactive token refresh before expiration
 */
function scheduleTokenRefresh(accessToken: string): void {
  // Clear any existing timer
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }

  const expirationTime = decodeJwtExpiration(accessToken);
  if (!expirationTime) {
    console.warn('[TokenRefresh] Could not decode token expiration, skipping proactive refresh');
    return;
  }

  const now = Date.now();
  const timeUntilExpiry = expirationTime - now;
  const refreshTime = timeUntilExpiry - REFRESH_BEFORE_EXPIRY_MS;

  // If token expires in less than 5 minutes, refresh immediately
  if (refreshTime <= 0) {
    console.warn('[TokenRefresh] Token expires soon, refreshing immediately');
    void refreshAccessToken().catch((error: unknown) => {
      console.error('[TokenRefresh] Proactive refresh failed:', error);
    });
    return;
  }

  console.warn(
    `[TokenRefresh] Scheduling proactive refresh in ${String(Math.round(refreshTime / 1000 / 60))} minutes`
  );

  refreshTimer = setTimeout(() => {
    console.warn('[TokenRefresh] Proactive refresh triggered');
    void refreshAccessToken().catch((error: unknown) => {
      console.error('[TokenRefresh] Proactive refresh failed:', error);
    });
  }, refreshTime);
}

/**
 * Stop scheduled token refresh
 */
function stopTokenRefresh(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

/**
 * Process all queued requests after token refresh completes
 */
function processQueue(error: unknown, token: string | null = null): void {
  failedQueue.forEach((request) => {
    if (error) {
      request.reject(error);
    } else {
      request.resolve(token);
    }
  });
  failedQueue = [];
}

/**
 * Check if a token refresh is currently in progress
 */
export function isTokenRefreshInProgress(): boolean {
  return isRefreshing;
}

/**
 * Wait for the current refresh to complete
 * Returns the new access token or throws if refresh fails
 */
export function waitForTokenRefresh(): Promise<string | null> {
  return new Promise((resolve, reject) => {
    failedQueue.push({ resolve, reject });
  });
}

/**
 * Perform token refresh
 *
 * @returns Promise with new access token
 * @throws Error if refresh fails (also logs out user)
 */
export async function refreshAccessToken(): Promise<string> {
  // If already refreshing, wait for it to complete
  if (isRefreshing) {
    const token = await waitForTokenRefresh();
    if (!token) {
      throw new Error('Token refresh failed');
    }
    return token;
  }

  isRefreshing = true;

  try {
    const { refreshToken } = useAuthStore.getState();

    if (!refreshToken) {
      console.warn('[TokenRefresh] No refresh token available, logging out');
      throw new Error('No refresh token available');
    }

    // Call SOKA API refresh endpoint directly (bypass axios to avoid recursion)
    const response = await fetch(`${SOKA_API_URL}/auth/refresh`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${refreshToken}`
      }
    });

    if (!response.ok) {
      throw new Error(`Refresh failed with status ${String(response.status)}`);
    }

    const responseData = (await response.json()) as TokenResponse | WrappedTokenResponse;

    // Handle both direct and wrapped response formats
    const newAccessToken =
      'accessToken' in responseData ? responseData.accessToken : responseData.data?.accessToken;
    const newRefreshToken =
      'refreshToken' in responseData ? responseData.refreshToken : responseData.data?.refreshToken;

    if (!newAccessToken || !newRefreshToken) {
      console.error('[TokenRefresh] Invalid refresh response format:', responseData);
      throw new Error('Invalid refresh response');
    }

    // Update store with new tokens
    useAuthStore.getState().setTokens(newAccessToken, newRefreshToken);

    // Schedule proactive refresh before new token expires
    scheduleTokenRefresh(newAccessToken);

    // Process queued requests with new token
    processQueue(null, newAccessToken);

    return newAccessToken;
  } catch (refreshError) {
    // Token refresh failed - logout user and redirect to login
    processQueue(refreshError, null);
    stopTokenRefresh(); // Stop any scheduled refresh
    useAuthStore.getState().logout();

    // Redirect to login page (hard redirect for clean slate)
    window.location.href = '/login';

    throw refreshError instanceof Error ? refreshError : new Error(String(refreshError));
  } finally {
    isRefreshing = false;
  }
}

/**
 * Start proactive token refresh for the current access token
 * Should be called when user logs in or app initializes with stored tokens
 */
export function startTokenRefreshScheduler(): void {
  const { accessToken } = useAuthStore.getState();
  if (accessToken) {
    scheduleTokenRefresh(accessToken);
  }
}

/**
 * Stop proactive token refresh
 * Should be called when user logs out
 */
export function stopTokenRefreshScheduler(): void {
  stopTokenRefresh();
}

/**
 * Check if token is expired or close to expiring
 * Returns true if token needs refresh
 */
function isTokenStale(token: string): boolean {
  const expirationTime = decodeJwtExpiration(token);
  if (!expirationTime) {
    // Can't decode expiration, assume it's stale
    return true;
  }

  const now = Date.now();
  const timeUntilExpiry = expirationTime - now;

  // Token is stale if it expires in less than 5 minutes
  return timeUntilExpiry <= REFRESH_BEFORE_EXPIRY_MS;
}

/**
 * Ensure token is valid before making API calls
 * If token is expired or close to expiring, refresh it
 * Call this when user returns after inactivity
 *
 * @returns Promise that resolves when token is valid
 */
export async function ensureValidToken(): Promise<void> {
  const { accessToken } = useAuthStore.getState();

  if (!accessToken) {
    // No token, nothing to validate
    return;
  }

  // Check if token is stale (expired or close to expiring)
  if (isTokenStale(accessToken)) {
    console.warn('[TokenRefresh] Token is stale, refreshing before API call');
    await refreshAccessToken();
  }
}
